import { RefString, RefDate, RefBool, Ref, Collection, Switch } from "@tblabs/truffle";
import { v4 as uuidv4 } from 'uuid';
import { BasketItem } from "../Models/Basket/BasketItem";
import { DeliveryForm } from "../Models/DeliveryForm";
import { PaymentForm } from "../Models/Payment/PaymentForm";
import { ReturnForm } from "../Models/ReturnForm";
import { Discount, Ticket } from "../Models/Ticket";
import { PaymentPresets } from "../Models/PaymentPresets";
import { BasketItemType } from "../Shop/BasketItemType";
import { RentBasketItem } from "../Models/Basket/RentBasketItem";
import { SaleBasketItem } from "../Models/Basket/SaleBasketItem";
import { DateTimeProvider } from "../Services/DateTimeProvider";


export class TicketData
{
    public orderId = new RefString(uuidv4())//.Storable("ticket-builder:order-id");
    public createdAt = new RefDate(DateTimeProvider.Now)//.Storable("ticket-builder:created-at");
    public withTraining = new RefBool(false)//.Storable("ticket-builder:with-training");
    public withInvoice = new RefBool(false)//.Storable("ticket-builder:with-invoice");
    public depositPaymentForm = new Ref<PaymentForm.Cash | PaymentForm.BankTransfer | PaymentForm.Crypto | PaymentForm.Blik>(PaymentForm.Cash)//.Storable("ticket-builder:deposit-payment-form");
    public basketPaymentForm = new Ref<PaymentForm.Cash | PaymentForm.CashAtDelivery | PaymentForm.BankTransfer | PaymentForm.Crypto | PaymentForm.Blik>(PaymentForm.Cash)//.Storable("ticket-builder:basket-payment-form");
    public paymentPresets = new Ref<PaymentPresets>(PaymentPresets.Unset)//.Storable("ticket-builder:payment-preset");
    public deliveryForm = new Ref<DeliveryForm>(DeliveryForm.Unset)//.Storable("ticket-builder:delivery-form");
    public returnForm = new Ref<ReturnForm>(ReturnForm.Unset)//.Storable("ticket-builder:return-form");
    public basketItems = new Collection<BasketItem>();
    public discounts = new Collection<Discount>();
    public customerName = new Ref<string>("").Storable("ticket-builder:customer-name");
    public customerEmail = new Ref<string>("").Storable("ticket-builder:customer-email");
    public customerPhone = new Ref<string>("").Storable("ticket-builder:customer-phone");
    public customerAddress = new Ref<string>("").Storable("ticket-builder:customer-address");
    public customerExperience = new Ref<number>(1).Storable("ticket-builder:customer-experience");
    public invoiceCompanyName = new Ref<string>("").Storable("ticket-builder:invoice-company-name");
    public invoiceCompanyAddress = new Ref<string>("").Storable("ticket-builder:invoice-company-address");
    public invoiceCompanyNIP = new Ref<string>("").Storable("ticket-builder:invoice-company-nip");

    constructor()
    {
        this.paymentPresets.OnChange(preset =>
        {
            switch (preset)
            {
                case PaymentPresets.AllWithCash:
                    this.depositPaymentForm.value = PaymentForm.Cash;
                    this.basketPaymentForm.value = PaymentForm.Cash;
                    break;
                case PaymentPresets.AllWithBankTransfer:
                    this.depositPaymentForm.value = PaymentForm.BankTransfer;
                    this.basketPaymentForm.value = PaymentForm.BankTransfer;
                    break;
                case PaymentPresets.AllWithCrypto:
                    this.depositPaymentForm.value = PaymentForm.Crypto;
                    this.basketPaymentForm.value = PaymentForm.Crypto;
                    break;
                case PaymentPresets.DepositWithBankTransfer_ServiceWithCash:
                    this.depositPaymentForm.value = PaymentForm.BankTransfer;
                    this.basketPaymentForm.value = PaymentForm.CashAtDelivery;
                    break;
                case PaymentPresets.DepositWithBlik_ServiceWithCash:
                    this.depositPaymentForm.value = PaymentForm.Blik;
                    this.basketPaymentForm.value = PaymentForm.CashAtDelivery;
                    break;
            }
        }, true);
    }

    public FromTicket(ticket: Ticket): void
    {
        this.orderId.value = ticket.Id;
        this.createdAt.value = ticket.Meta.CreatedAt;
        this.withTraining.value = ticket.WithTraining;
        this.withInvoice.value = ticket.Invoice !== undefined;
        this.invoiceCompanyName.value = ticket.Invoice?.CompanyName || "";
        this.invoiceCompanyAddress.value = ticket.Invoice?.CompanyAddress || "";
        this.invoiceCompanyNIP.value = ticket.Invoice?.CompanyNIP || "";
        this.basketItems.Load(
            ...ticket.BasketList
                .map(item => new Switch<BasketItemType, BasketItem>(item.Type)
                    .Case(BasketItemType.Rent, () => new RentBasketItem(item))
                    .Case(BasketItemType.Sale, () => new SaleBasketItem(item))
                    .Execute()).filter(x => x !== undefined));
        this.deliveryForm.value = ticket.Delivery;
        this.returnForm.value = ticket.Return;
        this.customerName.value = ticket.Customer.Name;
        this.customerEmail.value = ticket.Customer.Email;
        this.customerPhone.value = ticket.Customer.Phone;
        this.customerAddress.value = ticket.Customer.Address;
        this.customerExperience.value = ticket.Customer.Experience || 0;
        this.depositPaymentForm.value = ticket.DepositPaymentForm as PaymentForm.Cash | PaymentForm.BankTransfer | PaymentForm.Crypto | PaymentForm.Blik;
        this.basketPaymentForm.value = ticket.BasketPaymentForm as PaymentForm.Cash | PaymentForm.CashAtDelivery | PaymentForm.BankTransfer | PaymentForm.Crypto | PaymentForm.Blik;

        if (this.depositPaymentForm.Is(PaymentForm.Cash) && this.basketPaymentForm.Is(PaymentForm.Cash))
            this.paymentPresets.value = PaymentPresets.AllWithCash;
        else if (this.depositPaymentForm.Is(PaymentForm.BankTransfer) && this.basketPaymentForm.Is(PaymentForm.BankTransfer))
            this.paymentPresets.value = PaymentPresets.AllWithBankTransfer;
        else if (this.depositPaymentForm.Is(PaymentForm.Crypto) && this.basketPaymentForm.Is(PaymentForm.Crypto))
            this.paymentPresets.value = PaymentPresets.AllWithCrypto;
        else if (this.depositPaymentForm.Is(PaymentForm.BankTransfer) && this.basketPaymentForm.IsIn([PaymentForm.Cash, PaymentForm.CashAtDelivery]))
            this.paymentPresets.value = PaymentPresets.DepositWithBankTransfer_ServiceWithCash;
        else if (this.depositPaymentForm.Is(PaymentForm.Blik) && this.basketPaymentForm.IsIn([PaymentForm.Cash, PaymentForm.CashAtDelivery]))
            this.paymentPresets.value = PaymentPresets.DepositWithBlik_ServiceWithCash;
        else 
        {
            console.log("Unknown payment preset: deposit=", this.depositPaymentForm.value, ", basket=", this.basketPaymentForm.value);
            this.paymentPresets.value = PaymentPresets.Unset;
        }
    }

    public ToTicket(): Ticket
    {
        return ({
            Id: this.orderId.value,
            Meta: {
                CreatedAt: new Date(this.createdAt.value),
                CreatedBy: "generator",
            },
            BasketList: this.basketItems.Items.map(item => item.AsRaw),
            BasketInfo: {
                PriceBeforeDiscount: this.basketItems.Items.reduce((acc, item) => acc + item.TotalPrice.value, 0),
                PriceAfterDiscount: (-1),
                Discounts: this.discounts.Items.map(item => item.AsRaw),
            },
            Customer: {
                Name: this.customerName.value,
                Email: this.customerEmail.value,
                Phone: this.customerPhone.value,
                Address: this.customerAddress.value,
                Id: uuidv4(),
                Type: "Zamawiający",
                Experience: this.customerExperience.value,
                BankAccountNumber: "",
                Description: "",
            },
            Delivery: this.deliveryForm.value,
            Return: this.returnForm.value,
            DepositPaymentForm: this.depositPaymentForm.value,
            BasketPaymentForm: this.basketPaymentForm.value,
            WithTraining: this.withTraining.value,
            PickupPlace: "miejsce nieokreślone",
            Invoice: this.withInvoice.value ? {
                CompanyName: this.invoiceCompanyName.value,
                CompanyAddress: this.invoiceCompanyAddress.value,
                CompanyNIP: this.invoiceCompanyNIP.value,
            } : undefined,
            Notes: "",
        });
    }
}
